import { Avatar, Tooltip } from '@material-ui/core';
import ButtonBase from '@material-ui/core/ButtonBase';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { memo, useMemo, useRef, useState } from 'react';
import { aprenderColorMap } from '../../constants';
import './aprender.css';

const useStyles = makeStyles((theme) => ({
  imageContainer: ({ width, isPressed, horizontalPercentage = 50, verticalPercentage = 50 }) => ({
    position: 'relative',
    display: 'inline-block',
    width,
    height: 'auto',
    transform: isPressed ? 'scale(0.85)' : 'scale(1)',
    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.3s ease-in-out',
    overflow: 'hidden',
    willChange: 'transform',
    '&:hover': {
      transform: isPressed ? 'translateY(2px) scale(0.9)' : 'translateY(1px) scale(0.97)',
      filter: 'brightness(1.06)',
      zIndex: 10,
      boxShadow: '0 -0.5em 0.5em transparent, 0 0.5em 0.5em transparent, 0 0.20em 0.25em -0.2em hsla(var(--hue), var(--sat), 50%, 0.46), 0 0.20em 0.5em hsla(var(--hue), calc(var(--sat) - 10%), 40%, 0.3)',
    }
  }),
  topImage: ({
    width,
    isRound,
    horizontalPercentage = 50,
    verticalPercentage = 50,
    percentage = 0.85,
    topLeftRadius = 50,
    topRightRadius = 50,
    bottomLeftRadius = 50,
    bottomRightRadius = 50,
    imagePadding,
  }) => ({
    position: 'absolute',
    zIndex: 1,
    left: '50%',
    width: typeof width === 'number' ? `calc(${width}px * ${percentage})` : `${percentage * 100}%`,
    borderTopLeftRadius: isRound ? `${topLeftRadius}%` : 0,
    borderTopRightRadius: isRound ? `${topRightRadius}%` : 0,
    borderBottomLeftRadius: isRound ? `${bottomLeftRadius}%` : 0,
    borderBottomRightRadius: isRound ? `${bottomRightRadius}%` : 0,
    height: 'auto',
    transform: `translate(-${horizontalPercentage}%, -${verticalPercentage}%) scale(1)`,
    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    maxHeight: '100%',
    willChange: 'transform',
    objectFit: 'contain',
    [theme.breakpoints.between('sm', 'md')]: {
      width: `calc(${width === 220 ? 180 : width}px * ${percentage})`,
    },
    ...(imagePadding && {
      padding: imagePadding,
    }),
  }),
  focusVisible: {
    outline: 'none',
    borderRadius: '50%',
  },
}));

const StyledTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: '#1052BA',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    fontFamily: 'Janda Manatee Solid, sans-serif',
    letterSpacing: '0.04em',
  },
  arrow: {
    color: theme.palette.common.white,
    '&:before': { boxShadow: theme.shadows[1] },
  },
}))(Tooltip);

export function ColoredTooltip({ title, children, ...props }) {
  const stretchedTitle = (
    <span style={{ display: 'inline-block', transform: 'scaleX(1.1)' }}>
      {title}
    </span>
  );

  return (
    <StyledTooltip title={stretchedTitle} {...props}>
      {children}
    </StyledTooltip>
  );
}

function AprenderButton({
  color,
  image,
  title,
  width = 200,
  isRound,
  percentage = 0.85,
  customDimensions,
  horizontalPercentage = 50,
  verticalPercentage = 50,
  topRightRadius = 50,
  topLeftRadius = 50,
  bottomRightRadius = 50,
  bottomLeftRadius = 50,
  mini,
  size,
  tooltip = false,
  margin,
  imagePadding,
  ...props
}) {
  const [isPressed, setIsPressed] = useState(false);
  const [isLoading, setIsLoading] = useState(() => {
    const isSVG = image.endsWith('.svg');
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    return !(isSVG && isSafari);
  });
  const timeRef = useRef();

  const buttonColor = aprenderColorMap[color] || color || 'blue';

  const classes = useStyles({
    width,
    isPressed,
    isRound,
    percentage,
    horizontalPercentage,
    verticalPercentage,
    topRightRadius,
    topLeftRadius,
    bottomRightRadius,
    bottomLeftRadius,
    imagePadding,
  });

  const { isSVG, isSafari } = useMemo(() => {
    const svg = image.endsWith('.svg');
    const safari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    return { isSVG: svg, isSafari: safari };
  }, [image]);

  const handleImageLoad = () => setIsLoading(false);

  const handlePress = () => {
    setIsPressed(true);
    clearTimeout(timeRef.current);
    timeRef.current = setTimeout(() => setIsPressed(false), 300);
  };

  const ButtonContent = (
    <>
      {isLoading && <CircularProgress size={mini ? 20 : 40} />}
      {(!isSVG || !isSafari) && (
        <Avatar
          src={image}
          className={classes.topImage}
          imgProps={{
            onLoad: handleImageLoad,
            onError: handleImageLoad,
            alt: title,
            loading: 'lazy',
            decoding: 'async',
          }}
          style={isLoading ? { visibility: 'hidden' } : {}}
        />
      )}
      {isSVG && isSafari && (
        <object data={image} type="image/svg+xml" aria-label="button" className={classes.topImage} />
      )}
    </>
  );

  const ButtonBaseComponent = (
    <ButtonBase
      focusRipple={false}
      disableRipple
      className={clsx(
        classes.imageContainer,
        buttonColor.toLowerCase(),
        'aprender-button',
        mini && 'mini',
        size && `size-${size}`
      )}
      focusVisibleClassName={classes.focusVisible}
      onClick={props.onClick}
      onMouseDown={handlePress}
      style={{
        ...(margin && { margin }),
        ...(customDimensions && { width, height: width, ...customDimensions }),
      }}
      aria-label={title}
      aria-labelledby="aprender-button"
    >
      {ButtonContent}
    </ButtonBase>
  );

  return tooltip ? (
    <ColoredTooltip title={title} placement="top" arrow>
      {ButtonBaseComponent}
    </ColoredTooltip>
  ) : (
    ButtonBaseComponent
  );
}
export default memo(AprenderButton);
